import type { AlignTuning } from '../scenes/AlignScene';
import type { AlignDebugState } from '../scenes/AlignScene';

export function isDebugEnabled(): boolean {
  return new URLSearchParams(window.location.search).get('debug') === '1';
}

export function mountDebugPanel(
  panel: HTMLElement,
  tuning: AlignTuning,
  onChange: () => void,
  getStats: () => { fps: number; steps: number; align: AlignDebugState | null; paletteId: string },
  onReset: () => void,
  onNextPalette: () => void,
): () => void {
  panel.hidden = false;
  panel.innerHTML = `
    <div><strong>debug</strong> <span data-stats></span></div>
    <div data-palette style="margin:4px 0 6px;opacity:0.95"></div>
    <pre data-live style="margin:6px 0;white-space:pre-wrap;opacity:0.9"></pre>
    <button type="button" data-reset style="margin:4px 0;width:100%;cursor:pointer">RESET SCENE</button>
    <button type="button" data-next-palette style="margin:4px 0 8px;width:100%;cursor:pointer">NEXT PALETTE</button>
    ${slider('attraction', tuning.attraction, 200, 4000, 10)}
    ${slider('maxForce', tuning.maxForce, 200, 5000, 10)}
    ${slider('interactionRadius', tuning.interactionRadius, 80, 500, 5)}
    ${slider('innerSoftRadius', tuning.innerSoftRadius, 0, 80, 1)}
    ${slider('peakForceDistance', tuning.peakForceDistance, 20, 200, 2)}
    ${slider('linearDamping', tuning.linearDamping, 0, 6, 0.05)}
    ${slider('angularDamping', tuning.angularDamping, 0, 8, 0.05)}
    ${slider('torqueStrength', tuning.torqueStrength, 0, 3, 0.05)}
    ${slider('restitution', tuning.restitution, 0, 1, 0.01)}
    ${slider('snapDistanceRatio', tuning.snapDistanceRatio, 0.05, 0.4, 0.01)}
    ${slider('snapAngleDeg', tuning.snapAngleDeg, 4, 35, 1)}
    ${slider('softPull', tuning.softPull, 0, 1200, 10)}
    ${slider('softDamp', tuning.softDamp, 0, 10, 0.1)}
    ${slider('ambientPull', tuning.ambientPull, 0, 400, 5)}
    ${slider('repulsorInterval', tuning.repulsorInterval, 1, 8, 0.1)}
    ${slider('repulsorStrength', tuning.repulsorStrength, 0, 5000, 50)}
    ${slider('repulsorRadius', tuning.repulsorRadius, 40, 220, 5)}
  `;

  const inputs = panel.querySelectorAll<HTMLInputElement>('input[type="range"]');
  const onInput = (e: Event): void => {
    const input = e.target as HTMLInputElement;
    const key = input.dataset.key as keyof AlignTuning;
    const value = Number(input.value);
    if (key in tuning) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (tuning as any)[key] = value;
    }
    const label = input.parentElement?.querySelector('[data-val]');
    if (label) label.textContent = String(value);
    onChange();
  };
  inputs.forEach((el) => el.addEventListener('input', onInput));

  const resetBtn = panel.querySelector<HTMLButtonElement>('[data-reset]');
  const nextBtn = panel.querySelector<HTMLButtonElement>('[data-next-palette]');
  const onResetClick = (): void => onReset();
  const onNextClick = (): void => onNextPalette();
  resetBtn?.addEventListener('click', onResetClick);
  nextBtn?.addEventListener('click', onNextClick);

  let raf = 0;
  const tick = (): void => {
    const stats = getStats();
    const el = panel.querySelector('[data-stats]');
    if (el) el.textContent = `${stats.fps.toFixed(0)}fps · ${stats.steps} steps`;
    const pal = panel.querySelector('[data-palette]');
    if (pal) pal.textContent = `palette: ${stats.paletteId}`;
    const live = panel.querySelector('[data-live]');
    const a = stats.align;
    if (live && a) {
      live.textContent = [
        `state ${a.physicsState}`,
        `pos ${a.x.toFixed(0)}, ${a.y.toFixed(0)}`,
        `vel ${a.vx.toFixed(0)}, ${a.vy.toFixed(0)}  spd ${a.speed.toFixed(0)}`,
        `ang ${((a.angle * 180) / Math.PI) | 0}°  ω ${a.angularVelocity.toFixed(2)}`,
        `dist ${a.centerDist.toFixed(1)}  Δθ ${a.angleDeltaDeg.toFixed(1)}°`,
        `force ${a.force01.toFixed(2)}`,
      ].join('\n');
    }
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);

  return () => {
    cancelAnimationFrame(raf);
    inputs.forEach((el) => el.removeEventListener('input', onInput));
    resetBtn?.removeEventListener('click', onResetClick);
    nextBtn?.removeEventListener('click', onNextClick);
    panel.hidden = true;
    panel.innerHTML = '';
  };
}

function slider(key: string, value: number, min: number, max: number, step: number): string {
  return `<label>${key} <span data-val>${value}</span>
    <input type="range" data-key="${key}" min="${min}" max="${max}" step="${step}" value="${value}" />
  </label>`;
}
